import React, { useEffect, useState } from 'react';
import {
    Box,
    Flex,
    Text,
    Spinner,
    Divider,
    SimpleGrid
} from '@chakra-ui/react';
import { MdBlockFlipped, MdOutlineCancel } from "react-icons/md";
import { FiMessageSquare, FiDollarSign, FiCheckCircle, FiTarget } from "react-icons/fi";
import { TbActivityHeartbeat } from "react-icons/tb";
import { FaServer } from "react-icons/fa6";
import { StatCard } from '../StatsCard';
import InfoCard from '../IfoCard';
import ApiCallsChart from '../Charts/ApiCalls';
import { NotAllowedIcon, WarningIcon } from '@chakra-ui/icons';
import ChartSkeleton from '../Charts/Skeleton';
import { formatNumber, formatPrice } from '../../../utils/DefaultFunction';
import API from '../../../helpers/axiosInstance';
import { LuClock, LuTimer } from 'react-icons/lu';
import moment from 'moment';
import FifteenDaySummary from '../Charts/FifteenDaySummary';

const OverView = ({ date ,refresh}) => {
    
    const [loading, setLoading] = useState(false);
    const [overviewData, setOverviewData] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });
    const dateformated = moment(date);
    const month = dateformated.format("MM"); // "09"
    const year = dateformated.format("YYYY");

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/overview?month=${month}&year=${year}${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setOverviewData(response.data.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    if (loading || !overviewData) {
        return (
            <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" />
            </Flex>
        );
    }

    const colorThemes = [
        { bg: "#fef2f2", title: "#991b1b", value: "#b91c1c", subtitle: "#e11d48" },
        { bg: "#fff7ed", title: "#9a3412", value: "#c2410c", subtitle: "#ea580c" },
        { bg: "#fffbeb", title: "#854d0e", value: "#a16207", subtitle: "#ca8a04" },
        { bg: "var(--brand-tint)", title: "var(--brand-primary-hover)", value: "var(--brand-primary)", subtitle: "#3b82f6" },
        { bg: "var(--brand-50)", title: "var(--brand-800)", value: "var(--brand-primary-active)", subtitle: "var(--brand-primary-hover)" },
        { bg: "#fdf2f8", title: "#be185d", value: "#db2777", subtitle: "#ec4899" },
        { bg: "#faf5ff", title: "#6b21a8", value: "#7e22ce", subtitle: "#9333ea" },
        { bg: "#f0fdf4", title: "#166534", value: "#15803d", subtitle: "#22c55e" },
    ];

    function getRandomTheme() {
        return colorThemes[Math.floor(Math.random() * colorThemes.length)];
    }


    return (
        <>


            <Box>
                <SimpleGrid
                    columns={{ base: 1, sm: 2, md: 3, lg: 3, xl: 6 }}
                    spacing={2}
                >
                    <StatCard
                        title="Messages This Month"
                        value={formatNumber(overviewData.stat.monthly_message, "en-US", true)}
                        change={`${overviewData.stat.last_month_msg} from last month`}
                        icon={<FiMessageSquare />}
                        iconColor="gray.400"
                        originalValue={formatNumber(overviewData.stat.monthly_message)}
                    />
                    <StatCard
                        title="Sent"
                        value={`${overviewData.stat.sent_pending_percent}%`}
                        change={`${overviewData.stat.sent_pending} messages awaiting confirmation`}
                        icon={<LuClock />}
                        changeColor={"#f54a00"}
                        iconColor="#f54a00"
                        originalValue={formatNumber(overviewData.stat.sent_pending_percent)}
                    />
                    <StatCard
                        title="Delivery Success"
                        value={`${overviewData.stat.delivery_success}%`}
                        change={`${overviewData.stat.last_mont_delivery}% from last month`}
                        changeColor="#00a63e"
                        icon={<FiCheckCircle />}
                        iconColor="#00a63e"
                    />
                    <StatCard
                        title="Expired Messages"
                        value={`${overviewData.stat.expired_whatsapp_percent}%`}
                        change={`${overviewData.stat.expired_whatsapp} WhatsApp messages timed out`}
                        changeColor="#e17100"
                        icon={<LuTimer />}
                        iconColor="#e17100"
                    />
                    <StatCard
                        title="Failure Rate"
                        value={`${overviewData.account_health.failure_rate?.toFixed(2)}%`}
                        change={`${overviewData?.stat?.last_month_failure_rate?.toFixed(2)}% from last month`}
                        changeColor="#e7000b"
                        icon={<MdOutlineCancel />}
                        iconColor="#e7000b"
                    />
                    <StatCard
                        title="Monthly Savings"
                        value={`${formatPrice(overviewData.stat.monthly_saving?.toFixed(2), "en-US", overviewData?.customer_currency, true)}`}
                        change="vs. most expensive channel per message type"
                        changeColor="#00a63e"
                        icon={<FiDollarSign />}
                        iconColor="#00a63e"
                        originalValue={formatPrice(overviewData.stat.monthly_saving)}
                    />
                </SimpleGrid>
            </Box>



            {/* Account Health */}
            <Box className="box-style-card" my={4}>
                <Flex direction="column">
                    <Text fontSize="md" fontWeight="semibold" color="gray.700">Account Health Summary</Text>
                    <Text fontSize="sm" color="#64748b">Key performance indicators and subscriber statistics</Text>
                </Flex>

                <Flex gap={6} wrap="wrap" mb={8}>
                    {/* <InfoCard title="Failure Rate" value={`${overviewData.account_health.failure_rate?.toFixed(2)}%`} valueColor="red.500" center bg="#f1f5f980" /> */}
                    <InfoCard title="Overall Read Rate" value={`${overviewData.account_health.read_rate?.toFixed(2)}%`} valueColor="green.500" center bg="#f1f5f980" />
                    <InfoCard title="Avg Processing Time" value={`${overviewData.account_health.avg_process_time}s`} valueColor="black" center bg="#f1f5f980" />
                    <InfoCard title="Total Subscribers" value={formatNumber(overviewData.account_health.total_subscriber, "en-US", true)} valueColor="purple.500" center bg="#f1f5f980" />
                </Flex>
                <Divider my={8} />
                <Flex gap={6} wrap="wrap">
                    <InfoCard title="New This Month" subtitle="Subscriber growth" value={`${overviewData.account_health.new_subscriber > 0 ? `+${formatNumber(overviewData.account_health.new_subscriber,"en-US", true)}` : 0}`} valueColor="#009966" bg="#f0fdf4" spaceAround subtitleColor="green.400" />
                    <InfoCard title="Android Users" subtitle="Mobile platform" value={formatNumber(overviewData.account_health.android,"en-US", true)} valueColor="purple.600" bg="#faf5ff" spaceAround subtitleColor="purple.400" />
                    <InfoCard title="iOS Users" subtitle="Mobile platform" value={formatNumber(overviewData.account_health.ios,"en-US", true)} valueColor="orange.600" bg="#fffaf0" spaceAround subtitleColor="orange.400" />
                </Flex>
            </Box>
            <Box className="box-style-card" my={4}>
                <Flex direction="column">
                    <Flex align="center" gap={2}>
                        <TbActivityHeartbeat size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">Last 15 Days Message Volume</Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Daily message count breakdown by channel</Text>
                </Flex>
                <Flex gap={4} w="100%">
                    {/* Card 1 */}
                    <Box
                        bg="white"
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        textAlign="center"
                    >
                        <Text fontSize="xs" fontWeight="medium" color="gray.500">
                            Total Messages

                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="black" mt={1}>
                            {formatNumber(overviewData?.last15DaysSummary?.total_messages, "en-US", true)}
                        </Text>
                    </Box>

                    {/* Card 2 */}
                    <Box
                        bg="white"
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        textAlign="center"
                    >
                        <Text fontSize="xs" fontWeight="medium" color="gray.500">
                            Daily Average

                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="black" mt={1}>
                            {formatNumber(overviewData?.last15DaysSummary?.daily_average, "en-US", true)}
                        </Text>
                    </Box>

                    {/* Card 3 */}
                    <Box
                        bg="white"
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        textAlign="center"
                    >
                        <Text fontSize="xs" fontWeight="medium" color="gray.500">
                            Peak Day

                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="black" mt={1}>
                            {formatNumber(overviewData?.last15DaysSummary?.peak_day?.count, "en-US", true)}
                        </Text>
                    </Box>
                    <Box
                        bg="white"
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        textAlign="center"
                    >
                        <Text fontSize="xs" fontWeight="medium" color="gray.500">
                            Lowest Day


                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="black" mt={1}>
                            {formatNumber(overviewData?.last15DaysSummary?.lowest_day?.count, "en-US", true)}

                        </Text>
                    </Box>
                </Flex>
                <FifteenDaySummary data={overviewData?.last15DaysMessageVolume} />
            </Box>

            {/* API Stats */}
            <Box className="box-style-card" my={4}>
                {/* API Stats */}
                <Flex direction="column">
                    <Flex align="center" gap={2}>
                        <TbActivityHeartbeat size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">
                            API Stats
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Monitor API usage & rejections</Text>
                </Flex>

                <Flex gap={6} wrap="wrap" justifyContent="space-between" mt={4}>
                    <StatCard
                        title="Total API Calls"
                        subtitle={`${overviewData?.api_stat?.last_month_api_call}% from last month`}
                        value={formatNumber(overviewData.api_stat.total_api_call, "en-US", true)}
                        icon={<FaServer />}
                        iconColor="#155dfc"
                        bg="#f1f5f980"
                        changeColor="#155dfc"
                        originalValue={formatNumber(overviewData.api_stat.total_api_call)}
                    />
                    <StatCard
                        title="Requests Accepted"
                        subtitle={`${overviewData?.api_stat?.req_accepted_rate}% success rate`}
                        value={formatNumber(overviewData.api_stat.request_accepted, "en-US", true)}
                        icon={<FiCheckCircle />}
                        iconColor="#00a63e"
                        bg="#f1f5f980"
                        changeColor="#00a63e"
                        originalValue={formatNumber(overviewData.api_stat.request_accepted)}
                    />
                    <StatCard
                        title="Requests Rejected"
                        subtitle={`${overviewData?.api_stat?.req_rejected_rate}% rejection rate`}
                        value={overviewData.api_stat.request_rejected}
                        icon={<MdOutlineCancel />}
                        iconColor="#e7000b"
                        bg="#f1f5f980"
                        changeColor="#e7000b"
                    />
                    <StatCard
                        title="Requests Blocked"
                        subtitle={`${overviewData?.api_stat?.req_blocked_rate}% blocked rate`}
                        value={overviewData.api_stat.request_blocked}
                        icon={<MdBlockFlipped />}
                        iconColor="#9810fa"
                        bg="#f1f5f980"
                        changeColor="#9810fa"
                    />
                </Flex>

                {/* Rejection Reasons */}
                {overviewData.api_stat.rejectData.rejection?.length > 0 ? <Flex direction="column" mt={6}>
                    <Flex align="center" gap={2} mb={2}>
                        <WarningIcon color="orange.400" />
                        <Text fontSize="md" fontWeight="semibold">Rejection Reasons</Text>
                    </Flex>

                    <Flex gap={4} wrap="wrap">
                        {overviewData.api_stat.rejectData.rejection.map((item) => {
                            const theme = getRandomTheme();
                            return (
                                <InfoCard
                                    key={item.name}
                                    title={item.name}
                                    subtitle={item.description}
                                    value={item.count}
                                    titleColor={theme.title}
                                    valueColor={theme.value}
                                    subtitleColor={theme.subtitle}
                                    bg={theme.bg}
                                    spaceAround
                                />
                            );
                        })}
                    </Flex>
                </Flex> : ""}


                {overviewData.api_stat.rejectData.blocked?.length > 0 ? <Flex direction="column" mt={6}>
                    <Flex align="center" gap={2} mb={2}>
                        <NotAllowedIcon color="purple.400" />
                        <Text fontSize="md" fontWeight="semibold">Blocked Message Stats</Text>
                    </Flex>

                    <Flex gap={4} wrap="wrap">
                        {overviewData.api_stat.rejectData.blocked.map((item) => {
                            const theme = getRandomTheme();
                            return (
                                <InfoCard
                                    key={item.name}
                                    title={item.name}
                                    subtitle={item.description}
                                    value={item.count}
                                    titleColor={theme.title}
                                    valueColor={theme.value}
                                    subtitleColor={theme.subtitle}
                                    bg={theme.bg}
                                    spaceAround
                                />
                            );
                        })}
                    </Flex>
                </Flex> : ""}


            </Box>


            {/* 7-Day API Calls Trend */}
            <Box className="box-style-card" my={4}>
                <Flex direction="column">
                    <Flex align="center" gap={2}>
                        <TbActivityHeartbeat size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">7-Day API Calls Trend</Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Monitor your API usage</Text>
                </Flex>


                {overviewData.lastSevenDaysStats?.length > 0 ? <ApiCallsChart data={overviewData.lastSevenDaysStats} /> : <ChartSkeleton title={"API usage analytics will appear here"} />}

            </Box>

            {/* Campaign Stats */}
            <Box className="box-style-card" my={4}>
                <Flex direction="column">
                    <Text fontSize="md" fontWeight="semibold" color="gray.700">Campaign Stats</Text>
                    <Text fontSize="sm" color="#64748b">Overview of campaign performance</Text>
                </Flex>

                <Flex gap={6} wrap="wrap" mb={8}>
                    <StatCard title="Active Campaigns" value={overviewData.campaign_stat.active} icon={<FaServer />} subtitle={`${overviewData?.campaign_stat?.schedule_today} scheduled for today`} iconColor="#00a63e" bg="#f1f5f980" changeColor="#00a63e" />
                    <StatCard title="Completed This Month" value={overviewData.campaign_stat.complet_this_month} icon={<FiCheckCircle />} subtitle={`${overviewData?.campaign_stat?.complet_this_month} from last month`} iconColor="#155dfc" bg="#f1f5f980" changeColor="#155dfc" />
                    <StatCard title="Success Rate" value={`${overviewData.campaign_stat.success_rate}%`} icon={<FiTarget />} iconColor="#9810fa" bg="#f1f5f980" changeColor="#9810fa" subtitle={`Above target (${overviewData?.campaign_stat?.success_rate})`} />
                    {/* <StatCard title="Success Rate" value={overviewData.campaign_stat.schedule} icon={<MdOutlineCancel />} iconColor="#00a63e" bg="#f1f5f980" changeColor="#00a63e" /> */}
                </Flex>

                <Divider my={4} />
                {/* Row 2 - Colored background cards */}
                <Flex gap={6} wrap="wrap">
                    <InfoCard
                        title="Running"
                        subtitle="Currently active"
                        value={`${overviewData.campaign_stat.runing}`}
                        valueColor="#016630"
                        bg="#f0fdf4" // light green
                        spaceAround
                        subtitleColor={"#00a63e"}
                    />
                    <InfoCard
                        title="Scheduled"
                        subtitle="Upcoming launches"
                        value={`${overviewData.campaign_stat.schedule}`}

                        valueColor="#193cb8"
                        bg="var(--brand-tint)" // light purple
                        spaceAround
                        subtitleColor={"#155dfc"}
                    />
                    <InfoCard
                        title="Draft"
                        subtitle="In preparation"
                        value={`${overviewData.campaign_stat.draft}`}
                        valueColor="#9f2d00"
                        bg="#fff7ed" // light orange
                        spaceAround
                        subtitleColor={"#f54a00"}
                    />
                </Flex>
            </Box>
        </>
    );
};

export default OverView;
